import { Dispatch } from 'redux';
import { Project, Change, Board, Release } from '../../models';
import { Projects } from '../../services';
import { createRestSlices, applyReducers } from './rest';
import { ActionPayload } from '..';
import type { Status, Callback, Response } from './rest';
import { entityId, mergePopulatedChangeRefs, removeFromList, upsertList } from './remoteUpdate';

const SYNCING         = 'yoda/projects/SYNCING';
const SYNCING_SUCCESS = 'yoda/projects/SYNCING_SUCCESS';
const SYNCING_FAILURE = 'yoda/projects/SYNCING_FAILURE';
const SYNCING_RESET   = 'yoda/projects/SYNCING_RESET';

const UPDATING_MEMBERS           = 'yoda/projects/UPDATING_MEMBERS';
const UPDATING_MEMBERS_SUCCESS   = 'yoda/projects/UPDATING_MEMBERS_SUCCESS';
const UPDATING_MEMBERS_FAILURE   = 'yoda/projects/UPDATING_MEMBERS_FAILURE';
const UPDATING_MEMBERS_RESET     = 'yoda/projects/UPDATING_MEMBERS_RESET';

const GET_CHANGELOG         = 'yoda/projects/GET_CHANGELOG';
const GET_CHANGELOG_SUCCESS = 'yoda/projects/GET_CHANGELOG_SUCCESS';
const GET_CHANGELOG_FAILURE = 'yoda/projects/GET_CHANGELOG_FAILURE';
const GET_CHANGELOG_RESET   = 'yoda/projects/GET_CHANGELOG_RESET';

const GET_DIFF         = 'yoda/projects/GET_DIFF';
const GET_DIFF_SUCCESS = 'yoda/projects/GET_DIFF_SUCCESS';
const GET_DIFF_FAILURE = 'yoda/projects/GET_DIFF_FAILURE';
const GET_DIFF_RESET   = 'yoda/projects/GET_DIFF_RESET';

const GET_DOCUMENTATIONS         = 'yoda/projects/GET_DOCUMENTATIONS';
const GET_DOCUMENTATIONS_SUCCESS = 'yoda/projects/GET_DOCUMENTATIONS_SUCCESS';
const GET_DOCUMENTATIONS_FAILURE = 'yoda/projects/GET_DOCUMENTATIONS_FAILURE';

const SET_AVATAR           = 'yoda/projects/SET_AVATAR';
const SET_AVATAR_SUCCESS   = 'yoda/projects/SET_AVATAR_SUCCESS';
const SET_AVATAR_FAILURE   = 'yoda/projects/SET_AVATAR_FAILURE';

const ARCHIVING           = 'yoda/projects/ARCHIVING';
const ARCHIVING_SUCCESS   = 'yoda/projects/ARCHIVING_SUCCESS';
const ARCHIVING_FAILURE   = 'yoda/projects/ARCHIVING_FAILURE';
const ARCHIVING_RESET     = 'yoda/projects/ARCHIVING_RESET';

/**
 * Avatar upload returns an unpopulated project.display().
 * Keep the currently loaded (populated) project and only patch avatar.
 */
const patchProjectAvatar = (base: Project | null | undefined, incoming: AnyObj): Project => {
    if (base && base._id === incoming?._id)
        return new Project({ ...base, avatar: incoming.avatar });
    return new Project(incoming);
};

const {
    initialState,
    createReducer, startCreateReducer,
    getReducer, listReducer,
    updateReducer, deleteReducer,
    duplicateReducer,
    createAction, startCreateAction,
    getAction, listAction,
    updateAction, deleteAction,
    duplicateAction,
} = createRestSlices(Projects);

initialState.sync          = 'idle' as Status;
initialState.syncProjectId = null;
initialState.syncError     = null;

initialState.membersUpdate      = 'idle' as Status;
initialState.membersUpdateError = null;

initialState.getChangelog          = 'idle' as Status;
initialState.getChangelogProjectId = null;
initialState.getChangelogError     = null;
initialState.changelog = {
    project: null,
    releases: [],
    merged: [],
    upcoming: []
};

initialState.gettingDiff      = 'idle' as Status;
initialState.getDiffProjectId = null;
initialState.getDiffError     = null;
initialState.diff = {
    project: null,
    from: null,
    to: null,
    file: null,
    content: null
};

initialState.documentationsProjectId = null;
initialState.documentationsError     = null;

initialState.archiving      = 'idle' as Status;
initialState.archivingError = null;

const UPDATE = 'yoda/mercure/REMOTE_UPDATE';
const onRemoteUpdateReducer = (state = initialState, action: ActionPayload) => {
    if (action.type !== UPDATE)
        return state;

    const { update: { data, type }} = action;
    switch (type) {
        case 'project.created':
            return {
                ...state,
                projects: upsertList(state.projects, data, (payload) => new Project(payload), true)
            };
        case 'project.updated': {
            const projects = upsertList(state.projects, data, (payload) => new Project({
                ...(state.projects || []).find((project: Project) => project._id === payload._id) || {},
                ...payload
            }));
            if (!state.project || state.project._id !== data._id) {
                return { ...state, projects };
            }
            return {
                ...state,
                projects,
                project: new Project({
                    ...state.project,
                    ...data,
                })
            };
        }
        case 'change.updated':
        case 'change.created':
            if (!state.project || state.project._id !== entityId(data.project))
                return state;

            const changes = upsertList(
                state.project.changes,
                data,
                (payload) => {
                    const previous = (state.project.changes || []).find(
                        (item: Change) => item._id === payload._id
                    );
                    return new Change(mergePopulatedChangeRefs(payload, previous));
                },
                type === 'change.created'
            );
            return {
                ...state,
                project: new Project({
                    ...state.project,
                    changes
                })
            };
        case 'change.deleted':
            if (!state.project)
                return state;
            return {
                ...state,
                project: new Project({
                    ...state.project,
                    changes: removeFromList(state.project.changes, data._id)
                })
            };
        default:
            return state;
    }
};

/**
 * We have to add this hook to handle modifications on changes when we are
 * looking at a specific release...
 */
const UPDATING_CHANGE_SUCCESS = 'yoda/change/UPDATING_ITEM_SUCCESS';
const patchChange = (original: Change, change: Change) => (
    original._id === change._id ? new Change({ ...original, ...change }) : original
);

const updateChangeReducer = (state = initialState, action: ActionPayload) => {
    if (action.type !== UPDATING_CHANGE_SUCCESS)
        return state;

    const { change } = action;
    let next: typeof state = state;

    if (state.project?.releases) {
        const { project } = state;
        project.releases = project.releases.map((release: Release) => {
            release.changes = release.changes.map((original: Change) => patchChange(original, change));
            return release;
        });
        next = {
            ...next,
            project: new Project({ ...project })
        };
    }

    if (next.changelog?.releases || next.changelog?.upcoming) {
        next = {
            ...next,
            changelog: {
                ...next.changelog,
                releases: (next.changelog.releases || []).map((release: Release) => new Release({
                    ...release,
                    changes: (release.changes || []).map((original: Change) => patchChange(original, change))
                })),
                upcoming: (next.changelog.upcoming || []).map((original: Change) => patchChange(original, change))
            }
        };
    }

    return next;
};

/**
 * We have to add this hook to handle modifications on boards
 */
const UPDATING_BOARD_SUCCESS = 'yoda/board/UPDATING_ITEM_SUCCESS';
const CREATING_BOARD_SUCCESS = `yoda/board/CREATING_ITEM_SUCCESS`;
const DELETING_BOARD_SUCCESS = `yoda/board/DELETING_ITEM_SUCCESS`;
const updateBoardReducer = (state = initialState, action: ActionPayload) => {
    if (!state.project || !state.project.boards)
        return state;

    const { board } = action;
    const { project } = state;

    switch (action.type) {
        case UPDATING_BOARD_SUCCESS:

            // let's replace the original board in the project
            // with the updated one
            project.boards = project.boards.map((original: Board) => {
                return original._id === board._id ? new Board(board) : original;
            });
            return {
                ...state,
                project: new Project({ ...project })
            };
        case CREATING_BOARD_SUCCESS:
            return {
                ...state,
                project: new Project({
                    ...project,
                    boards: [
                        ...project.boards,
                        new Board(board)
                    ]
                })
            };
        case DELETING_BOARD_SUCCESS:
            return {
                ...state,
                project: new Project({
                    ...project,
                    boards: project.boards.filter((b: Board) => b._id !== board._id)
                })
            };
        default:
            return state;
    }
};

/**
 * We have to add this hook to handle attachments creation/deletion
 */
const CREATING_ATTTACHMENT_SUCCESS = `yoda/attachments/CREATING_ITEM_SUCCESS`;
const DELETING_ATTTACHMENT_SUCCESS = `yoda/attachment/DELETING_ITEM_SUCCESS`;
const SET_PROJECT_NB_ATTACHMENTS = 'yoda/project/SET_NB_ATTACHMENTS';
const attachmentsReducer = (state = initialState, action: ActionPayload) => {
    if (!state.project)
        return state;

    switch (action.type) {
        case CREATING_ATTTACHMENT_SUCCESS:
            return {
                ...state,
                project: new Project({
                    ...state.project,
                    nbAttachments: state.project.nbAttachments + 1
                })
            };
        case DELETING_ATTTACHMENT_SUCCESS:
            return {
                ...state,
                project: new Project({
                    ...state.project,
                    nbAttachments: state.project.nbAttachments - 1
                })
            };
        case SET_PROJECT_NB_ATTACHMENTS:
            return {
                ...state,
                project: new Project({
                    ...state.project,
                    nbAttachments: action.nbAttachments
                })
            };
        default:
            return state;
    }
};

const avatarReducer = (state = initialState, action: ActionPayload) => {
    switch (action.type) {
        case SET_AVATAR:
            return {
                ...state,
                projectsUpdate: 'pending',
                projectsUpdateError: null
            };
        case SET_AVATAR_SUCCESS:
            return {
                ...state,
                projectsUpdate: 'succeeded',
                projectsUpdateError: null,
                project: patchProjectAvatar(state.project, action.project),
                projects: (state.projects || []).map((p: Project) => (
                    p._id === action.project?._id
                        ? patchProjectAvatar(p, action.project)
                        : p
                ))
            };
        case SET_AVATAR_FAILURE:
            return {
                ...state,
                projectsUpdate: 'failed',
                projectsUpdateError: action.error
            };
        default:
            return state;
    }
};

const archivingReducer = (state = initialState, action: ActionPayload) => {
    switch (action.type) {
        case ARCHIVING:
            return {
                ...state,
                archiving: 'pending',
                archivingError: null
            };
        case ARCHIVING_SUCCESS:
            return {
                ...state,
                archiving: 'succeeded',
                archivingError: null,
                project: (state.project?._id === action.project._id) ? action.project : state.project,
                projects: (state.projects || []).map((p: Project) => (
                    p._id === action.project?._id ? action.project : p
                ))
            };
        case ARCHIVING_FAILURE:
            return {
                ...state,
                archiving: 'failed',
                archivingError: action.error
            };
        case ARCHIVING_RESET:
            return {
                ...state,
                archiving: 'idle',
                archivingError: null
            };
        default:
            return state;
    }
};

const syncReducer = (state = initialState, action: ActionPayload) => {
    switch (action.type) {
        case SYNCING:
            return {
                ...state,
                sync: 'pending',
                syncProjectId: action.id,
                syncError: null
            };
        case SYNCING_SUCCESS:
            return {
                ...state,
                sync: 'succeeded',
                syncError: null,
                project: (state.project?._id === action.project._id) ? action.project : state.project,
                projects: (state.projects || []).map((p: Project) => {
                    if (p._id === action.project?._id)
                        return action.project;
                    return p;
                })
            };
        case SYNCING_FAILURE:
            return {
                ...state,
                sync: 'failed',
                syncError: action.error
            };
        case SYNCING_RESET:
            return {
                ...state,
                sync: 'idle',
                syncProjectId: null,
                syncError: null
            };
        default:
            return state;
    }
};

const membersUpdateReducer = (state = initialState, action: ActionPayload) => {
    switch (action.type) {
        case UPDATING_MEMBERS:
            return {
                ...state,
                membersUpdate: 'pending',
                membersUpdateError: null
            };
        case UPDATING_MEMBERS_SUCCESS:
            return {
                ...state,
                membersUpdate: 'succeeded',
                membersUpdateError: null,
                project: action.project,
                projects: (state.projects || []).map((p: Project) => {
                    if (p._id === action.project?._id)
                        return action.project;
                    return p;
                })
            };
        case UPDATING_MEMBERS_FAILURE:
            return {
                ...state,
                membersUpdate: 'failed',
                membersUpdateError: action.error
            };
        case UPDATING_MEMBERS_RESET:
            return {
                ...state,
                membersUpdate: 'idle',
                syncProjectId: null,
                membersUpdateError: null
            };
        default:
            return state;
    }
};

const getChangelogReducer = (state = initialState, action: ActionPayload) => {
    switch (action.type) {
        case GET_CHANGELOG:
            return {
                ...state,
                getChangelog: 'pending',
                getChangelogProjectId: action.projectId,
                getChangelogError: null
            };
        case GET_CHANGELOG_SUCCESS:
            return {
                ...state,
                getChangelog: 'succeeded',
                getChangelogError: null,
                changelog: action.changelog
            };
        case GET_CHANGELOG_FAILURE:
            return {
                ...state,
                getChangelog: 'failed',
                getChangelogError: action.error
            };
        case GET_CHANGELOG_RESET:
            return {
                ...state,
                getChangelog: 'idle',
                getChangelogProjectId: null,
                getChangelogError: null,
                changelog: {
                    project: null,
                    releases: [],
                    merged: [],
                    upcoming: []
                }
            };
        default:
            return state;
    }
};

const getDiffReducer = (state = initialState, action: ActionPayload) => {
    switch (action.type) {
        case GET_DIFF:
            return {
                ...state,
                gettingDiff: 'pending',
                getDiffProjectId: action.projectId,
                getDiffError: null
            };
        case GET_DIFF_SUCCESS:
            return {
                ...state,
                gettingDiff: 'succeeded',
                getDiffError: null,
                diff: action.diff
            };
        case GET_DIFF_FAILURE:
            return {
                ...state,
                gettingDiff: 'failed',
                getDiffError: action.error
            };
        case GET_DIFF_RESET:
            return {
                ...state,
                gettingDiff: 'idle',
                getDiffProjectId: null,
                getDiffError: null,
                diff: {
                    from: null,
                    to: null,
                    file: null,
                    content: null
                }
            };
        default:
            return state;
    }
};

const getDocumentationsReducer = (state = initialState, action: ActionPayload) => {
    switch (action.type) {
        case GET_DOCUMENTATIONS:
            return {
                ...state,
                getDocumentations: 'pending',
                documentationsProjectId: action.projectId,
                documentationsError: null
            };
        case GET_DOCUMENTATIONS_SUCCESS:
            return {
                ...state,
                getDocumentations: 'succeeded',
                documentationsError: null,
                project: state.documentationsProjectId === state.project?._id ? new Project({
                    ...state.project,
                    documentations: action.documentations
                }) : state.project
            };
        case GET_DOCUMENTATIONS_FAILURE:
            return {
                ...state,
                getDocumentations: 'failed',
                documentationsError: action.error
            };
        default:
            return state;
    }
};

/* Export reducer */
/* eslint import/no-anonymous-default-export: [2, {"allowArrowFunction": true}] */
export default (state = initialState, action: ActionPayload) => {
    return applyReducers(state, action, [
        createReducer, startCreateReducer,
        getReducer, listReducer, avatarReducer, archivingReducer,
        updateReducer, deleteReducer, onRemoteUpdateReducer,
        duplicateReducer, syncReducer,
        updateChangeReducer, updateBoardReducer, membersUpdateReducer,
        getChangelogReducer, getDocumentationsReducer, attachmentsReducer,
        getDiffReducer
    ]);
}

const syncAction = (id: string, forceRefresh: boolean, callback?: Callback) => {
    return (dispatch: Dispatch) => {
        dispatch({type: SYNCING, id});
        return Projects.sync(id, forceRefresh)
            .then((data: Response) => {
                const {project} = data;
                dispatch({type: SYNCING_SUCCESS, project});
                setTimeout(() => {
                    dispatch({type: SYNCING_RESET});
                }, 3000);
                callback && callback(/*err*/null);
            })
            .catch((error: Error) => {
                dispatch({type: SYNCING_FAILURE, error: error.message});
                setTimeout(() => {
                    dispatch({type: SYNCING_RESET});
                }, 3000);
                callback && callback(error);
            });
    }
};

const addMembersAction = (id: string, members: string[], callback?: Callback) => {
    return (dispatch: Dispatch) => {
        dispatch({type: UPDATING_MEMBERS});
        return Projects.addMembers(id, members)
            .then((data: Response) => {
                const {project} = data;
                dispatch({type: UPDATING_MEMBERS_SUCCESS, project});
                setTimeout(() => {
                    dispatch({type: UPDATING_MEMBERS_RESET});
                }, 100);
                callback && callback(/*err*/null);
            })
            .catch((error: Error) => {
                dispatch({type: UPDATING_MEMBERS_FAILURE, error: error.message});
                setTimeout(() => {
                    dispatch({type: UPDATING_MEMBERS_RESET});
                }, 100);
                callback && callback(error);
            });
    }
};

const removeMembersAction = (id: string, members: string[], callback?: Callback) => {
    return (dispatch: Dispatch) => {
        dispatch({type: UPDATING_MEMBERS});
        return Projects.removeMembers(id, members)
            .then((data: Response) => {
                const {project} = data;
                dispatch({type: UPDATING_MEMBERS_SUCCESS, project});
                setTimeout(() => {
                    dispatch({type: UPDATING_MEMBERS_RESET});
                }, 100);
                callback && callback(/*err*/null);
            })
            .catch((error: Error) => {
                dispatch({type: UPDATING_MEMBERS_FAILURE, error: error.message});
                setTimeout(() => {
                    dispatch({type: UPDATING_MEMBERS_RESET});
                }, 100);
                callback && callback(error);
            });
    }
};

const getChangelogAction = (projectId: string, from?: string, merge?: string[], callback?: Callback) => {
    return (dispatch: Dispatch) => {
        dispatch({type: GET_CHANGELOG, projectId});
        return Projects.getChangelog(projectId, from, merge)
            .then((data: Response) => {
                const {changelog} = data;
                dispatch({type: GET_CHANGELOG_SUCCESS, changelog});
                callback && callback(/*err*/null);
            })
            .catch((error: Error) => {
                dispatch({type: GET_CHANGELOG_FAILURE, error: error.message});
                callback && callback(error);
            });
    }
};

const getDiffAction = (projectId: string, from: string, to: string, file?: string, callback?: Callback) => {
    return (dispatch: Dispatch) => {
        dispatch({type: GET_DIFF, projectId});
        return Projects.getDiff(projectId, from, to, file)
            .then((data: Response) => {
                const { diff } = data;
                dispatch({type: GET_DIFF_SUCCESS, diff});
                callback && callback(/*err*/null);
            })
            .catch((error: Error) => {
                dispatch({type: GET_DIFF_FAILURE, error: error.message});
                callback && callback(error);
            });
    }
};

const getDocumentationsAction = (projectId: string, callback?: Callback) => {
    return (dispatch: Dispatch) => {
        dispatch({type: GET_DOCUMENTATIONS, projectId});
        return Projects.getDocumentations(projectId)
            .then((data: Response) => {
                const {documentations} = data;
                dispatch({type: GET_DOCUMENTATIONS_SUCCESS, documentations});
                callback && callback(/*err*/null);
            })
            .catch((error: Error) => {
                dispatch({type: GET_DOCUMENTATIONS_FAILURE, error: error.message});
                callback && callback(error);
            });
    }
}

const setAvatarAction = (id: string, file: File, callback?: Callback) => {

    return (dispatch: Dispatch) => {
        dispatch({type: SET_AVATAR});
        return Projects.setAvatar(id, file).then((data: AnyObj) => {
            const { project } = data;
            dispatch({type: SET_AVATAR_SUCCESS, project});
            callback && callback(/*err*/null);
        })
        .catch((error: Error) => {
            dispatch({type: SET_AVATAR_FAILURE, error: error.message});
            callback && callback(error);
        });
    }
}

const unsetAvatarAction = (id: string, callback?: Callback) => {

    return (dispatch: Dispatch) => {
        dispatch({type: SET_AVATAR});
        return Projects.unsetAvatar(id).then((data: AnyObj) => {
            const { project } = data;
            dispatch({type: SET_AVATAR_SUCCESS, project});
            callback && callback(/*err*/null);
        })
        .catch((error: Error) => {
            dispatch({type: SET_AVATAR_FAILURE, error: error.message});
            callback && callback(error);
        });
    }
}

const deactivateProjectAction = (id: string, callback?: Callback) => {
    return (dispatch: Dispatch) => {
        dispatch({type: ARCHIVING});
        return Projects.deactivate(id)
            .then((data: Response) => {
                const { project } = data;
                dispatch({type: ARCHIVING_SUCCESS, project});
                setTimeout(() => {
                    dispatch({type: ARCHIVING_RESET});
                }, 100);
                callback && callback(/*err*/null);
            })
            .catch((error: Error) => {
                dispatch({type: ARCHIVING_FAILURE, error: error.message});
                setTimeout(() => {
                    dispatch({type: ARCHIVING_RESET});
                }, 100);
                callback && callback(error);
            });
    };
};

const activateProjectAction = (id: string, callback?: Callback) => {
    return (dispatch: Dispatch) => {
        dispatch({type: ARCHIVING});
        return Projects.activate(id)
            .then((data: Response) => {
                const { project } = data;
                dispatch({type: ARCHIVING_SUCCESS, project});
                setTimeout(() => {
                    dispatch({type: ARCHIVING_RESET});
                }, 100);
                callback && callback(/*err*/null);
            })
            .catch((error: Error) => {
                dispatch({type: ARCHIVING_FAILURE, error: error.message});
                setTimeout(() => {
                    dispatch({type: ARCHIVING_RESET});
                }, 100);
                callback && callback(error);
            });
    };
};

/* Export CRUD actions */
export const createProject        = createAction;
export const startCreateProject   = startCreateAction;
export const loadProject          = getAction;
export const loadProjects         = listAction;
export const updateProject        = updateAction;
export const deleteProject        = deleteAction;
export const duplicateProject     = duplicateAction;
export const syncProject          = syncAction;
export const deactivateProject    = deactivateProjectAction;
export const activateProject      = activateProjectAction;
export const addProjectMembers    = addMembersAction;
export const removeProjectMembers = removeMembersAction;
export const getChangelog         = getChangelogAction;
export const getDiff              = getDiffAction;
export const getDocumentations    = getDocumentationsAction;
export const setProjectAvatar     = setAvatarAction;
export const unsetProjectAvatar   = unsetAvatarAction;
export const setProjectNbAttachments = (nbAttachments: number) => ({
    type: SET_PROJECT_NB_ATTACHMENTS,
    nbAttachments
});
